import React, {CSSProperties, useCallback, useEffect, useRef} from 'react';
import {observer} from "mobx-react";
import {SparkSwiperSlideBox} from "./boxes/spark-swiper-slide.box";
import {ISparkSwiperSlideController} from "./models/spark-swiper-slide-controller.interface";
import {SPARK_SWIPE_DELTA_THRESHOLD} from "./spark-swiper-consts";
import {ISparkSwiperController} from "./models/spark-swiper-controller.interface";

interface SparkSwiperSlideComponentProps {
    slideController: ISparkSwiperSlideController;
    swiperController: ISparkSwiperController;
    touchMoveDelta: number;
    animationStarted: boolean;
    onTransitionEnd?: () => void;
}
export const SparkSwiperSlideComponent: React.FC<SparkSwiperSlideComponentProps> = observer((props) => {
    const elementRef = useRef<HTMLDivElement>();
    const {swiperController, slideController, touchMoveDelta, animationStarted} = props;

    const deltaSign =  Math.sign(touchMoveDelta);

    const computeTranslate = useCallback((indexOffset: number) => {
        const percentage = indexOffset * 100;

        if (animationStarted) {
            if(Math.abs(touchMoveDelta) > SPARK_SWIPE_DELTA_THRESHOLD) {
                if(slideController.itemIndex === 0 && slideController.isActiveSlide && deltaSign === 1) {
                    // when the first slide is the active one and the user try to move up (previous; deltaSign === 1)
                    // then we return to 0% translation.
                    // if we don't do this then the below `translateY(${percentage + deltaSign * 100}%)` will be translateY(100%) because percentage will be 0 since we are on the active slide (indexOffset === 0)
                    // and then on the next rerender will revert to 0% and the animation will look strange (go to 100% and then back to 0%)
                    return `translateY(${percentage}%)`;
                } else if(slideController.itemIndex === swiperController.maxItemIndex && slideController.isActiveSlide && deltaSign === -1) {
                    // when the last slide is the active one and the user try to move down (next; deltaSign === -1)
                    // then we return to 0% translation.
                    // if we don't do this then the below `translateY(${percentage + deltaSign * 100}%)` will be translateY(-100%) because percentage will be 0 since we are on the active slide (indexOffset === 0)
                    // and then on the next rerender will revert to 0% and the animation will look strange (go to -100% and then back to 0%)
                    return `translateY(${percentage}%)`;
                }
                // animate to the next/prev slide
                return `translateY(${percentage + deltaSign * 100}%)`;
            }
            // Not enough swipe, animate to the original point
            return `translateY(${percentage}%)`;
        }

        if(touchMoveDelta === 0) {
            return `translateY(${percentage}%)`;
        }

        return `translateY(calc(${percentage}% + ${touchMoveDelta}px))`;

    }, [touchMoveDelta, animationStarted, deltaSign, swiperController.maxItemIndex, slideController.itemIndex, slideController.isActiveSlide]);

    useEffect(() => {
        if(elementRef.current) {
            if(animationStarted) {
                elementRef.current.style.transition = 'transform 0.25s ease-in-out';
            } else {
                elementRef.current.style.transition = 'unset';
            }
            elementRef.current.style.transform = computeTranslate(slideController.itemIndexOffset);
        }
    }, [slideController.itemIndexOffset, computeTranslate, animationStarted]);

    const styles: CSSProperties = {
        transform: computeTranslate(slideController.itemIndexOffset)
    };

    return (
        <SparkSwiperSlideBox className={slideController.key}
                             key={slideController.key}
                             ref={elementRef}
                             style={styles}
                             onTransitionEnd={props.onTransitionEnd}>
            {slideController.renderSlideContent()}
        </SparkSwiperSlideBox>
    )
})